<div class="settings-shell">
  <div class="settings-shell-header" *ngIf="!userId">
    <h1>Settings</h1>
    <p>Capture, tracking, schedule, and who gets Daily Insights.</p>
  </div>

  <div class="settings-layout" *ngIf="!userId">
    <nav class="settings-nav" aria-label="Settings sections">
      <ng-container *ngFor="let section of settingsSections">
        <button type="button" class="settings-nav-btn" *ngIf="isSettingsSectionVisible(section.id)"
          [class.active]="activeTab === section.id"
          (click)="setActiveTab(section.id, section.id === 'bulk' ? entitlementKeys.bulk_settings_update : null)">
          <mat-icon>{{ section.icon }}</mat-icon>
          <span class="settings-nav-label">{{ section.label }}</span>
          <mat-icon *ngIf="isSectionLocked(section.id)" class="tab-lock-icon">lock</mat-icon>
        </button>
      </ng-container>
    </nav>

    <div class="settings-main">
  <!-- Bulk update -->
  <div *ngIf="activeTab === 'bulk' && showBulkUpdateTab()">
    <div class="settings-panel settings-panel--bulk">
      <div class="settings-job-header">
        <h2>Bulk update</h2>
        <p>Choose people or teams, then apply capture and tracking settings.</p>
      </div>
      <app-entitlement-lock-banner *ngIf="isSettingsFeatureLocked(entitlementKeys.bulk_settings_update)"
        [entitlementKey]="entitlementKeys.bulk_settings_update" returnUrl="/employee/setting">
      </app-entitlement-lock-banner>
      <app-bulk-update-settings *ngIf="canBulkSettingsUpdate()"></app-bulk-update-settings>
    </div>
  </div>

  <form *ngIf="globalSettings" [formGroup]="globalSettings">

    <!-- Insights & delivery -->
    <div *ngIf="activeTab === 'delivery' && isAdmin">
      <div class="settings-panel settings-panel--delivery">
        <div class="settings-job-header">
          <h2>Delivery</h2>
          <p>Who gets Daily Insights — and optional tools next to SuperSee.</p>
        </div>

        <!-- Email -->
        <section class="delivery-block">
          <div class="delivery-block-head">
            <h4>Email recipients</h4>
            <p>Up to 5 addresses · {{ reportRecipientEmails.length }}/5</p>
          </div>
          <div class="delivery-block-body">
            <div class="delivery-email-row">
              <input type="email" class="delivery-email-input browser-default" #emailInput
                [formControl]="emailInputControl"
                placeholder="name@company.com"
                autocomplete="email"
                (keydown.enter)="$event.preventDefault(); addEmailFromInput(emailInput)"
                (paste)="onPaste($event)" />
              <button type="button" class="delivery-add-btn"
                (click)="addEmailFromInput(emailInput)"
                [disabled]="reportRecipientEmails.length >= 5">
                Add
              </button>
            </div>
            <p class="delivery-hint delivery-hint--error"
              *ngIf="emailInputControl.hasError('email') && emailInputControl.touched">
              Enter a valid email
            </p>

            <div class="delivery-chips" *ngIf="reportRecipientEmails.length > 0">
              <mat-chip *ngFor="let email of reportRecipientEmails; let i = index" [removable]="true"
                (removed)="removeEmail(i)" class="recipient-chip">
                {{ email }}
                <mat-icon matChipRemove>cancel</mat-icon>
              </mat-chip>
            </div>
            <p class="delivery-empty" *ngIf="reportRecipientEmails.length === 0">No recipients yet</p>
            <p class="delivery-hint">If mail is missing, check spam and mark SuperSee as safe.</p>
          </div>
        </section>

        <!-- WhatsApp -->
        <section class="delivery-block">
          <div class="delivery-block-head">
            <h4>WhatsApp</h4>
            <p>Same short Daily Insights overview · up to 3 numbers</p>
          </div>
          <div class="delivery-block-body">
            <div class="delivery-toggle-row">
              <div class="delivery-toggle-copy">
                <strong>Get Daily Insights on WhatsApp</strong>
                <span>Optional — never blocks other settings</span>
              </div>
              <mat-slide-toggle color="primary" [checked]="whatsappOptedIn"
                (change)="onWhatsappOptInChange($event.checked)"
                aria-label="Get Daily Insights on WhatsApp"></mat-slide-toggle>
            </div>

            <div class="whatsapp-phone-row">
              <mat-select class="whatsapp-phone-dial" panelClass="journey-phone-dial-panel"
                [ngModel]="whatsappDialCode" (ngModelChange)="onWhatsappDialChange($event)"
                [ngModelOptions]="{standalone: true}" aria-label="Country code" disableOptionCentering>
                <mat-select-trigger>
                  <span class="whatsapp-phone-dial-trigger">{{ whatsappDialDisplay }}</span>
                </mat-select-trigger>
                <mat-option *ngFor="let c of whatsappCountries" [value]="c.dialCode">
                  {{ c.code }} {{ c.dialCode }} — {{ c.name }}
                </mat-option>
              </mat-select>
              <span class="whatsapp-phone-divider" aria-hidden="true"></span>
              <input type="tel" class="whatsapp-phone-national browser-default" [(ngModel)]="whatsappNational"
                [ngModelOptions]="{standalone: true}" name="whatsappNational" maxlength="14"
                placeholder="Mobile number" autocomplete="tel-national" inputmode="numeric"
                (keydown.enter)="$event.preventDefault(); addWhatsappFromPicker()" />
              <button type="button" class="whatsapp-phone-add-btn"
                (click)="addWhatsappFromPicker()"
                [disabled]="!canAddWhatsappNumber">
                Add
              </button>
            </div>
            <p class="delivery-hint">{{ whatsappNumbers.length }}/3 numbers</p>

            <div class="delivery-chips" *ngIf="whatsappNumbers.length > 0">
              <mat-chip *ngFor="let number of whatsappNumbers; let i = index" [removable]="true"
                (removed)="removeWhatsappNumber(i)" class="recipient-chip">
                {{ number }}
                <mat-icon matChipRemove>cancel</mat-icon>
              </mat-chip>
            </div>
            <p class="delivery-empty" *ngIf="whatsappNumbers.length === 0">No numbers yet</p>
          </div>
        </section>

        <!-- Example preview (collapsed, capped) -->
        <details class="delivery-preview">
          <summary>
            <span>Example Daily Insights</span>
            <span class="delivery-preview-badge">Sample · not your data</span>
          </summary>
          <div class="delivery-preview-frame">
            <iframe src="./assets/docs/SuperSee_Daily_Insights_for_2026-09-01_SuperSee.html"
              title="Example Daily Insights" class="demo-iframe" loading="lazy"></iframe>
          </div>
        </details>

        <!-- Integrations -->
        <section class="delivery-block">
          <div class="delivery-block-head">
            <h4>Integrations</h4>
            <p>Optional tools beside SuperSee activity</p>
          </div>
          <div class="delivery-block-body delivery-block-body--flush">
            <div class="delivery-integration" aria-label="Conduct Workspace">
              <img src="../../../assets/rao_conduct_logo_blue.png" class="delivery-integration-logo" alt="" />
              <div class="delivery-integration-copy">
                <div class="delivery-integration-title">
                  <strong>Conduct Workspace</strong>
                  <span class="delivery-status" [class.is-on]="isConductIntegrated" role="status">
                    {{ isCheckingConductStatus ? 'Checking…' : conductStatusError ? 'Unavailable' :
                    isConductIntegrated ? 'Connected' : isConductEnabled ? 'Not connected' : 'Access required' }}
                  </span>
                </div>
                <p>Projects and tasks next to work activity in SuperSee.</p>
                <p *ngIf="!isCheckingConductStatus && !conductStatusError && !isConductEnabled" class="delivery-hint">
                  Ask us on WhatsApp to enable Conduct for your company.
                </p>
              </div>
              <div class="delivery-integration-actions">
                <mat-spinner *ngIf="isCheckingConductStatus" diameter="20"
                  aria-label="Checking Conduct access"></mat-spinner>
                <button type="button" *ngIf="conductStatusError && !isCheckingConductStatus" class="integration-action"
                  (click)="checkConductIntegrationStatus()">Retry</button>
                <a *ngIf="!isCheckingConductStatus && !conductStatusError && !isConductEnabled"
                  class="integration-action integration-primary" target="_blank" rel="noopener noreferrer"
                  href="https://api.whatsapp.com/send?phone=916354719703&text=Hi%2C%20please%20enable%20Conduct%20integration%20for%20my%20SuperSee%20company.">
                  Contact on WhatsApp
                </a>
                <ng-container *ngIf="!isCheckingConductStatus && !conductStatusError && isConductEnabled">
                  <ng-container *ngIf="isConductIntegrated; else showIntegrateBtnDelivery">
                    <button type="button" class="integration-action" (click)="goToConduct()"
                      [disabled]="isNavigatingToConduct || isDisconnectingConduct">
                      <mat-spinner *ngIf="isNavigatingToConduct" diameter="16"></mat-spinner>
                      Open Conduct
                    </button>
                    <button type="button" class="integration-action integration-disconnect" (click)="disconnectConduct()"
                      [disabled]="isDisconnectingConduct || isNavigatingToConduct">
                      <mat-spinner *ngIf="isDisconnectingConduct" diameter="16"></mat-spinner>
                      Disconnect
                    </button>
                  </ng-container>
                  <ng-template #showIntegrateBtnDelivery>
                    <button type="button" class="integration-action integration-primary" (click)="integrateConduct()"
                      [disabled]="isIntegratingConduct">
                      <mat-spinner *ngIf="isIntegratingConduct" diameter="16"></mat-spinner>
                      Connect
                    </button>
                  </ng-template>
                </ng-container>
              </div>
            </div>

            <div class="delivery-integration" aria-labelledby="custom-integration-heading">
              <div class="delivery-integration-icon" aria-hidden="true">
                <mat-icon>extension</mat-icon>
              </div>
              <div class="delivery-integration-copy">
                <strong id="custom-integration-heading">Custom integration</strong>
                <p>Need something else wired into your workflow?</p>
              </div>
              <div class="delivery-integration-actions">
                <a class="integration-action integration-wa"
                  href="https://api.whatsapp.com/send?phone=916354719703&text=Hi,%20I%20got%20this%20number%20from%20SuperSee%20Integration"
                  target="_blank" rel="noopener noreferrer">
                  Contact us
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Schedule -->
    <div *ngIf="activeTab === 'schedule' && isAdmin">
      <div class="settings-panel settings-panel--schedule">
        <div class="settings-job-header settings-job-header--row">
          <div>
            <h2>Schedule</h2>
            <p>
              <ng-container *ngIf="canMultipleShifts(); else basicShiftSubtitle">
                Work shifts that drive attendance and Daily Insights.
              </ng-container>
              <ng-template #basicShiftSubtitle>
                Configure your organization work shift (1 shift on Basic plan).
              </ng-template>
            </p>
          </div>
          <button type="button" class="create-shift-btn"
            [disabled]="shiftLimitReached || isLoadingShifts || isSubmittingShift" [matTooltip]="shiftLimitTooltip"
            matTooltipPosition="above" (click)="showShiftDrawer = true">
            <mat-icon>add</mat-icon>
            Create New Shift
          </button>
        </div>

        <app-entitlement-lock-banner *ngIf="shiftLimitReached && !isLoadingShifts"
          [entitlementKey]="entitlementKeys.multiple_shifts" returnUrl="/employee/setting">
        </app-entitlement-lock-banner>

        <div *ngIf="isLoadingShifts" class="shift-loading-state">
          <mat-spinner diameter="36"></mat-spinner>
          <p>Loading shifts…</p>
        </div>

        <div *ngIf="!isLoadingShifts && shifts.length > 0" class="schedule-shift-list">
          <app-shift-management [shifts]="shifts" [isLoading]="isLoadingShifts"
            [shiftLimitReached]="shiftLimitReached" (refreshShifts)="loadShifts($event)"
            (editShiftEvent)="editShift($event)">
          </app-shift-management>
        </div>

        <div *ngIf="!isLoadingShifts && shifts.length === 0" class="shift-empty-state-box">
          <div class="shift-clock-icon-wrapper">
            <mat-icon class="shift-clock-icon">schedule</mat-icon>
          </div>
          <h3 class="shift-empty-title">No shifts yet</h3>
          <p class="shift-empty-desc">Create a shift to track work hours and power Daily Insights.</p>
          <button type="button" class="create-first-shift-btn" (click)="showShiftDrawer = true">
            Create first shift
          </button>
        </div>

        <app-shift-drawer [isOpen]="showShiftDrawer" [editingShift]="editingShift" [isEditMode]="isEditMode"
          [employees]="shiftEmployees" (closed)="closeShiftDrawer()" (shiftCreated)="onShiftCreated($event)"
          (shiftUpdated)="onShiftUpdated($event)">
        </app-shift-drawer>
      </div>
    </div>

    <!-- Capture -->
    <div *ngIf="activeTab === 'capture' && isAdmin">
      <div class="settings-panel">
        <div class="settings-job-header">
          <h2>Capture</h2>
          <p>What SuperSee records from the team — screenshots, idle, and optional extras.</p>
        </div>

        <div class="settings-option-list">
          <div class="settings-option-row">
            <div class="settings-option-body">
              <h3 class="settings-option-title">Periodic screenshots</h3>
              <p class="settings-option-desc">Timed screenshots for steady work tracking</p>
            </div>
            <div class="settings-option-control">
              <mat-slide-toggle color="primary" formControlName="ss_periodic"
                (change)="isPeriodicToggleChange($event)"></mat-slide-toggle>
            </div>
          </div>

          <div class="settings-nested" *ngIf="globalSettings.get('ss_periodic')?.value">
            <div class="settings-nested-row">
              <div class="settings-nested-label">
                <strong>Interval <span class="settings-field-badge">{{ screenshotIntervalBadgeLabel() }}</span></strong>
                <span>Suggested every 30 min</span>
                <span class="plan-hint-text" *ngIf="screenshotIntervalPlanHint()">{{ screenshotIntervalPlanHint() }}</span>
              </div>
              <div class="interval-stepper">
                <button type="button" class="stepper-btn"
                  [disabled]="isScreenshotIntervalDecrementDisabled()"
                  (click)="decrementScreenshotInterval($event)">−</button>
                <div class="stepper-value">
                  <input type="number" class="stepper-value-input"
                    [value]="getDisplayMinutes('ss_screen_capture_time', 15)"
                    (input)="setNewMinutes('ss_screen_capture_time', $any($event.target).value)"
                    (change)="setNewMinutes('ss_screen_capture_time', $any($event.target).value)"
                    [attr.min]="minScreenshotCaptureMinutes()" max="60"
                    aria-label="Periodic screenshots interval in minutes">
                  <span class="unit">min</span>
                </div>
                <button type="button" class="stepper-btn"
                  [disabled]="getDisplayMinutes('ss_screen_capture_time', 15) >= 60"
                  (click)="incrementMinutes('ss_screen_capture_time', 1)">+</button>
              </div>
            </div>
          </div>

          <div class="settings-option-row">
            <div class="settings-option-body">
              <h3 class="settings-option-title">Idle timeout</h3>
              <p class="settings-option-desc">Suggested 5 min · no keyboard or mouse activity</p>
            </div>
            <div class="settings-option-control">
              <div class="interval-stepper">
                <button type="button" class="stepper-btn" [disabled]="getDisplayMinutes('ss_idle_time', 5) == 1"
                  (click)="incrementMinutes('ss_idle_time', -1)">−</button>
                <div class="stepper-value">
                  <input type="number" class="stepper-value-input"
                    [value]="getDisplayMinutes('ss_idle_time', 5)"
                    (change)="setNewMinutes('ss_idle_time', $event.target.value)" min="1" max="60"
                    aria-label="System idle timeout in minutes"
                    oninput="if(value >= 60) value = 60; if(value < 1) value = 1;">
                  <span class="unit">min</span>
                </div>
                <button type="button" class="stepper-btn" [disabled]="getDisplayMinutes('ss_idle_time', 5) >= 60"
                  (click)="incrementMinutes('ss_idle_time', 1)">+</button>
              </div>
            </div>
          </div>
        </div>

        <details class="settings-advanced" [open]="showCaptureAdvanced || globalSettings.get('ss_appchange')?.value || isWebcamEnabled()">
          <summary>Advanced</summary>
          <div class="settings-advanced-body settings-option-list">
            <div class="settings-option-row" [class.is-locked]="!canAdvancedScreenshotModes()">
              <div class="settings-option-body">
                <h3 class="settings-option-title">App-switch screenshots
                  <span class="settings-field-badge">{{ advancedModesBadgeLabel() }}</span>
                </h3>
                <p class="settings-option-desc" *ngIf="canAdvancedScreenshotModes()">Capture when someone switches apps</p>
                <p class="settings-option-desc" *ngIf="!canAdvancedScreenshotModes()">{{ advancedScreenshotModesUpgradeMessage() }}</p>
              </div>
              <div class="settings-option-control">
                <mat-slide-toggle color="primary" formControlName="ss_appchange"
                  (change)="isAppChangeToggleChange($event)"></mat-slide-toggle>
              </div>
            </div>

            <div class="settings-nested" *ngIf="globalSettings.get('ss_appchange')?.value">
              <div class="settings-nested-row">
                <div class="settings-nested-label">
                  <strong>Delay after switch</strong>
                  <span>Suggested 5 min</span>
                </div>
                <div class="interval-stepper">
                  <button type="button" class="stepper-btn"
                    [disabled]="getDisplayMinutes('ss_appchange_interval', 5) == 1"
                    (click)="incrementMinutes('ss_appchange_interval', -1)">−</button>
                  <div class="stepper-value">
                    <input type="number" class="stepper-value-input"
                      [value]="getDisplayMinutes('ss_appchange_interval', 5)"
                      (change)="setNewMinutes('ss_appchange_interval', $event.target.value)" min="1" max="60"
                      aria-label="App switch capture interval in minutes"
                      oninput="if(value >= 60) value = 60; if(value < 1) value = 1;">
                    <span class="unit">min</span>
                  </div>
                  <button type="button" class="stepper-btn"
                    [disabled]="getDisplayMinutes('ss_appchange_interval', 5) >= 60"
                    (click)="incrementMinutes('ss_appchange_interval', 1)">+</button>
                </div>
              </div>
            </div>

            <div class="settings-option-row" [class.is-locked]="!isWebcamCaptureEntitled">
              <div class="settings-option-body">
                <h3 class="settings-option-title">Webcam photos
                  <span class="settings-field-badge">{{ webcamBadgeLabel() }}</span>
                </h3>
                <p class="settings-option-desc">Optional snapshots to verify presence</p>
              </div>
              <div class="settings-option-control">
                <mat-slide-toggle color="primary" formControlName="ss_webcam"></mat-slide-toggle>
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>

    <!-- Tracking -->
    <div *ngIf="activeTab === 'tracking' && isAdmin">
      <div class="settings-panel">
        <div class="settings-job-header">
          <h2>Tracking</h2>
          <p>Control how the desktop agent behaves during the workday.</p>
        </div>
        <app-entitlement-lock-banner *ngIf="isSettingsFeatureLocked(entitlementKeys.advanced_tracking_controls)"
          [entitlementKey]="entitlementKeys.advanced_tracking_controls" returnUrl="/employee/setting">
        </app-entitlement-lock-banner>
        <div class="settings-option-list" *ngIf="canAdvancedTrackingControls()">
          <div class="settings-option-row">
            <div class="settings-option-body">
              <h3 class="settings-option-title">Silent mode</h3>
              <p class="settings-option-desc">Background tracking with limited employee controls</p>
            </div>
            <div class="settings-option-control">
              <mat-slide-toggle color="primary" (change)="silentModeToggleChange($event)"
                formControlName="ss_silent"></mat-slide-toggle>
            </div>
          </div>
          <div class="settings-option-row">
            <div class="settings-option-body">
              <h3 class="settings-option-title">Strict time tracking</h3>
              <p class="settings-option-desc">Sign out if system time or timezone changes</p>
            </div>
            <div class="settings-option-control">
              <mat-slide-toggle color="primary" (change)="strictTimeWarning($event)"
                formControlName="ss_strict_time_tracking"></mat-slide-toggle>
            </div>
          </div>
          <div class="settings-option-row">
            <div class="settings-option-body">
              <h3 class="settings-option-title">Auto tracking</h3>
              <p class="settings-option-desc">Starts automatically — employees can’t start or stop</p>
            </div>
            <div class="settings-option-control">
              <mat-slide-toggle color="primary" (change)="automaticTimeWarning($event)"
                formControlName="ss_automatic_tracking"></mat-slide-toggle>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dirty-only save dock (Schedule saves in the shift drawer) -->
    <div class="settings-save-dock" *ngIf="!userId && showSettingsSaveDock" role="status">
      <div class="settings-save-dock-inner">
        <p class="settings-save-status">{{ settingsSaveStatus }}</p>
        <div class="settings-save-actions">
          <button type="button" class="settings-save-secondary" *ngIf="activeTab === 'bulk'"
            (click)="resetBulkSettings()">
            Reset
          </button>
          <button type="button" class="settings-save-primary"
            [disabled]="!canSettingsSave"
            (click)="onSettingsSave()">
            {{ settingsSaveLabel }}
          </button>
        </div>
      </div>
    </div>
    </form>
    </div>
  </div>


    <!-- PERSONALIZED USER SETTINGS -->
    <div *ngIf="userId" class="settings-employee">
      <div class="settings-shell-header">
        <h1>{{userName}} settings</h1>
        <p>Override company defaults for this person when personalize is on.</p>
      </div>
      <form *ngIf="globalSettings" [formGroup]="globalSettings">
        <div class="settings-panel">
          <div class="personalizeSettingDiv" style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:8px;">
            <div class="settings-header-content" style="display:flex;align-items:center;gap:12px;">
              <div class="profile-image-wrapper">
                <img *ngIf="this.userData?.meta?.profile_image_url && this.userData?.meta?.profile_image_url !== 'null' && this.userData?.meta?.profile_image_url !== null"
                  [src]="this.userData?.meta?.profile_image_url" class="settings-profile-image" alt="Profile" />
                <ngx-avatars *ngIf="!this.userData?.meta?.profile_image_url || this.userData?.meta?.profile_image_url === 'null' || this.userData?.meta?.profile_image_url === null"
                  name="{{ this.userData?.name }}" size="48"></ngx-avatars>
              </div>
              <div class="profile-name-section">
                <h3 style="cursor: pointer;" class="user-name" [routerLink]="['/user-details/', userId]">{{userName}} Settings</h3>
                <h6 class="user-role">{{userData?.role | getUserRole}}</h6>
              </div>
            </div>
            <span class="personalize-toggle">
              <mat-slide-toggle color="primary" formControlName="ss_personalizeSettings"
                (change)="isPersonalizesToggleChange($event)"></mat-slide-toggle>
            </span>
          </div>
        </div>

        <nav class="settings-nav settings-nav--inline" aria-label="Employee settings sections" style="position:static;flex-direction:row;margin-bottom:16px;">
          <button type="button" class="settings-nav-btn" [class.active]="activeUserTab === 'capture'"
            (click)="setActiveUserTab('capture')">
            <mat-icon>photo_camera</mat-icon>
            <span class="settings-nav-copy">
              <span class="settings-nav-label">Capture</span>
            </span>
          </button>
          <button type="button" class="settings-nav-btn" [class.active]="activeUserTab === 'tracking'"
            (click)="setActiveUserTab('tracking')">
            <mat-icon>tune</mat-icon>
            <span class="settings-nav-copy">
              <span class="settings-nav-label">Tracking</span>
            </span>
          </button>
        </nav>

        <div *ngIf="activeUserTab === 'capture' || !activeUserTab" class="settings-panel">
          <div class="settings-job-header">
            <h2>Capture</h2>
            <p>What SuperSee records for this person.</p>
            <p class="plan-hint-text" *ngIf="showPlanRetentionSummary()">{{ planRetentionSummaryLabel() }}</p>
          </div>
          <div class="settings-option-list">
            <div class="settings-option-row">
              <div class="settings-option-body">
                <h3 class="settings-option-title">Periodic screenshots</h3>
                <p class="settings-option-desc">Timed screenshots for steady work tracking</p>
              </div>
              <div class="settings-option-control">
                <mat-slide-toggle color="primary" formControlName="ss_periodic"
                  (change)="isPeriodicToggleChange($event)"></mat-slide-toggle>
              </div>
            </div>

            <div class="settings-nested" *ngIf="globalSettings.get('ss_periodic')?.value"
              [class.interval-item-disabled]="!globalSettings.get('ss_personalizeSettings')?.value">
              <div class="settings-nested-row">
                <div class="settings-nested-label">
                  <strong>Interval <span class="settings-field-badge">{{ screenshotIntervalBadgeLabel() }}</span></strong>
                  <span>Suggested every 30 min</span>
                  <span class="plan-hint-text" *ngIf="screenshotIntervalPlanHint()">{{ screenshotIntervalPlanHint() }}</span>
                </div>
                <div class="interval-stepper">
                  <button type="button" class="stepper-btn"
                    [disabled]="isScreenshotIntervalDecrementDisabled() || !globalSettings.get('ss_personalizeSettings')?.value"
                    (click)="decrementScreenshotInterval($event)">−</button>
                  <div class="stepper-value">
                    <input type="number" class="stepper-value-input"
                      [value]="getDisplayMinutes('ss_screen_capture_time', 15)"
                      [disabled]="!globalSettings.get('ss_personalizeSettings')?.value"
                      (input)="setNewMinutes('ss_screen_capture_time', $any($event.target).value)"
                      (change)="setNewMinutes('ss_screen_capture_time', $any($event.target).value)"
                      [attr.min]="minScreenshotCaptureMinutes()" max="60"
                      aria-label="Periodic screenshots interval in minutes">
                    <span class="unit">min</span>
                  </div>
                  <button type="button" class="stepper-btn"
                    [disabled]="getDisplayMinutes('ss_screen_capture_time', 15) >= 60 || !globalSettings.get('ss_personalizeSettings')?.value"
                    (click)="incrementMinutes('ss_screen_capture_time', 1)">+</button>
                </div>
              </div>
              <span *ngIf="globalSettings.get('ss_screen_capture_time').errors && globalSettings.get('ss_screen_capture_time').touched">
                <mat-hint style="color: rgb(255, 0, 0);"> Invalid time format</mat-hint>
              </span>
              <span *ngIf="globalSettings.get('ss_screen_capture_time').hasError('invalidTime')">
                <mat-hint style="color: rgb(255, 0, 0);"> Minimum 1 minute required </mat-hint>
              </span>
            </div>

            <div class="settings-option-row">
              <div class="settings-option-body">
                <h3 class="settings-option-title">Idle timeout</h3>
                <p class="settings-option-desc">Suggested 5 min · no keyboard or mouse activity</p>
              </div>
              <div class="settings-option-control">
                <div class="interval-stepper">
                  <button type="button" class="stepper-btn"
                    [disabled]="getDisplayMinutes('ss_idle_time', 5) == 1 || !globalSettings.get('ss_personalizeSettings')?.value"
                    (click)="incrementMinutes('ss_idle_time', -1)">−</button>
                  <div class="stepper-value">
                    <input type="number" class="stepper-value-input"
                      [value]="getDisplayMinutes('ss_idle_time', 5)"
                      (change)="setNewMinutes('ss_idle_time', $event.target.value)" min="1" max="60"
                      [disabled]="!globalSettings.get('ss_personalizeSettings')?.value"
                      aria-label="System idle timeout in minutes"
                      oninput="if(value >= 60) value = 60; if(value < 1) value = 1;">
                    <span class="unit">min</span>
                  </div>
                  <button type="button" class="stepper-btn"
                    [disabled]="getDisplayMinutes('ss_idle_time', 5) >= 60 || !globalSettings.get('ss_personalizeSettings')?.value"
                    (click)="incrementMinutes('ss_idle_time', 1)">+</button>
                </div>
              </div>
            </div>
            <span *ngIf="globalSettings.get('ss_idle_time').errors && globalSettings.get('ss_idle_time').touched">
              <mat-hint style="color: rgb(255, 0, 0);"> Invalid time format</mat-hint>
            </span>
            <span *ngIf="globalSettings.get('ss_idle_time').hasError('invalidTime')">
              <mat-hint style="color: rgb(255, 0, 0);"> Minimum 1 minute required </mat-hint>
            </span>
          </div>

          <details class="settings-advanced" [open]="globalSettings.get('ss_appchange')?.value || isWebcamEnabled()">
            <summary>Advanced</summary>
            <div class="settings-advanced-body settings-option-list">
              <div class="settings-option-row" [class.is-locked]="!canAdvancedScreenshotModes()">
                <div class="settings-option-body">
                  <h3 class="settings-option-title">App-switch screenshots
                    <span class="settings-field-badge">{{ advancedModesBadgeLabel() }}</span>
                  </h3>
                  <p class="settings-option-desc" *ngIf="canAdvancedScreenshotModes()">Capture when this person switches apps</p>
                  <p class="settings-option-desc" *ngIf="!canAdvancedScreenshotModes()">{{ advancedScreenshotModesUpgradeMessage() }}</p>
                </div>
                <div class="settings-option-control">
                  <mat-slide-toggle color="primary" formControlName="ss_appchange"
                    (change)="isAppChangeToggleChange($event)"></mat-slide-toggle>
                </div>
              </div>

              <div class="settings-nested" *ngIf="globalSettings.get('ss_appchange')?.value"
                [class.interval-item-disabled]="!globalSettings.get('ss_personalizeSettings')?.value">
                <div class="settings-nested-row">
                  <div class="settings-nested-label">
                    <strong>Delay after switch</strong>
                    <span>Suggested 5 min</span>
                  </div>
                  <div class="interval-stepper">
                    <button type="button" class="stepper-btn"
                      [disabled]="getDisplayMinutes('ss_appchange_interval', 5) == 1 || !globalSettings.get('ss_personalizeSettings')?.value"
                      (click)="incrementMinutes('ss_appchange_interval', -1)">−</button>
                    <div class="stepper-value">
                      <input type="number" class="stepper-value-input"
                        [value]="getDisplayMinutes('ss_appchange_interval', 5)"
                        [disabled]="!globalSettings.get('ss_personalizeSettings')?.value"
                        (change)="setNewMinutes('ss_appchange_interval', $event.target.value)" min="1" max="60"
                        aria-label="App switch capture interval in minutes"
                        oninput="if(value >= 60) value = 60; if(value < 1) value = 1;">
                      <span class="unit">min</span>
                    </div>
                    <button type="button" class="stepper-btn"
                      [disabled]="getDisplayMinutes('ss_appchange_interval', 5) >= 60 || !globalSettings.get('ss_personalizeSettings')?.value"
                      (click)="incrementMinutes('ss_appchange_interval', 1)">+</button>
                  </div>
                </div>
                <span *ngIf="globalSettings.get('ss_appchange_interval').errors && globalSettings.get('ss_appchange_interval').touched">
                  <mat-hint style="color: rgb(255, 0, 0);"> Invalid time format</mat-hint>
                </span>
                <span *ngIf="globalSettings.get('ss_appchange_interval').hasError('invalidTime')">
                  <mat-hint style="color: rgb(255, 0, 0);"> Minimum 1 minute required</mat-hint>
                </span>
              </div>

              <div class="settings-option-row" [class.is-locked]="!isWebcamCaptureEntitled">
                <div class="settings-option-body">
                  <h3 class="settings-option-title">Webcam photos
                    <span class="settings-field-badge">{{ webcamBadgeLabel() }}</span>
                  </h3>
                  <p class="settings-option-desc">Optional snapshots for this person</p>
                </div>
                <div class="settings-option-control">
                  <mat-slide-toggle color="primary" formControlName="ss_webcam"></mat-slide-toggle>
                </div>
              </div>
            </div>
          </details>
        </div>

        <div *ngIf="activeUserTab === 'tracking'" class="settings-panel">
          <div class="settings-job-header">
            <h2>Tracking</h2>
            <p>How the agent behaves for this person.</p>
          </div>
          <app-entitlement-lock-banner *ngIf="isSettingsFeatureLocked(entitlementKeys.advanced_tracking_controls)"
            [entitlementKey]="entitlementKeys.advanced_tracking_controls" returnUrl="/employee/setting">
          </app-entitlement-lock-banner>
          <div class="settings-option-list" *ngIf="canAdvancedTrackingControls()">
            <div class="settings-option-row">
              <div class="settings-option-body">
                <h3 class="settings-option-title">Silent mode</h3>
                <p class="settings-option-desc">Background tracking with limited employee controls</p>
              </div>
              <div class="settings-option-control">
                <mat-slide-toggle color="primary" (change)="silentModeToggleChange($event)"
                  formControlName="ss_silent"></mat-slide-toggle>
              </div>
            </div>
            <div class="settings-option-row">
              <div class="settings-option-body">
                <h3 class="settings-option-title">Strict time tracking</h3>
                <p class="settings-option-desc">Sign out if system time or timezone changes</p>
              </div>
              <div class="settings-option-control">
                <mat-slide-toggle color="primary" (change)="strictTimeWarning($event)"
                  formControlName="ss_strict_time_tracking"></mat-slide-toggle>
              </div>
            </div>
            <div class="settings-option-row">
              <div class="settings-option-body">
                <h3 class="settings-option-title">Auto tracking</h3>
                <p class="settings-option-desc">Starts automatically — employees can’t start or stop</p>
              </div>
              <div class="settings-option-control">
                <mat-slide-toggle color="primary" (change)="automaticTimeWarning($event)"
                  formControlName="ss_automatic_tracking"></mat-slide-toggle>
              </div>
            </div>
          </div>
        </div>

        <div class="settings-save-dock" *ngIf="showEmployeeSaveDock" role="status">
          <div class="settings-save-dock-inner">
            <p class="settings-save-status">Unsaved changes for this person</p>
            <div class="settings-save-actions">
              <button type="button" class="settings-save-primary"
                [disabled]="!globalSettings?.dirty || globalSettings?.invalid"
                (click)="submitGlobalConfig('save')">
                Save
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
</div>
